/**
 * Phase 19.10 — Add-to-Playlist dialog for beats.
 *
 * Opens from BeatCard / BeatDetailPage. Shows the viewer's beat playlists
 * with quick-add toggles, and a "Create new playlist" inline composer.
 * Producer/Artist separation: this dialog talks ONLY to /api/beat-playlists
 * (artist playlists at /api/playlists are kept separate).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { optimizeImage } from "@/lib/mediaUrls";

const AddBeatToPlaylistDialog = ({ open, onClose, beat }) => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (!open) {
      setNewName(""); setCreating(false); setBusyId(null);
      return;
    }
    setLoading(true);
    api.get("/beat-playlists/my")
      .then((r) => setPlaylists(r.data?.playlists || []))
      .catch((e) => {
        if (e?.response?.status === 401) {
          toast.error("Sign in to use playlists");
        } else {
          toast.error(e?.response?.data?.detail || "Could not load your playlists");
        }
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleAdd = async (pl) => {
    if (!beat?.beat_id) return;
    setBusyId(pl.playlist_id);
    try {
      const r = await api.post(`/beat-playlists/${pl.playlist_id}/beats`, { beat_id: beat.beat_id });
      if (r.data?.duplicate) {
        toast(`Already in "${pl.name}"`);
      } else {
        toast.success(`Added to "${pl.name}"`);
      }
      // Update local state — bump count
      setPlaylists((ps) => ps.map((p) =>
        p.playlist_id === pl.playlist_id
          ? { ...p, beat_count: (p.beat_count || 0) + (r.data?.duplicate ? 0 : 1),
              beat_ids: r.data?.duplicate ? (p.beat_ids || []) : [...(p.beat_ids || []), beat.beat_id] }
          : p));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add to playlist");
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const r = await api.post("/beat-playlists", {
        name, is_public: true, beat_ids: beat?.beat_id ? [beat.beat_id] : [],
      });
      const pl = r.data;
      setPlaylists((ps) => [pl, ...ps]);
      setNewName("");
      toast.success(beat?.beat_id ? `Created "${name}" and added beat` : `Created "${name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create playlist");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="add-beat-to-playlist-dialog"
    >
      <div
        className="w-full max-w-md bg-[#161620] border border-purple-500/40 rounded-xl p-5 max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-white">
            🎼 Add to Playlist
          </h2>
          <button onClick={onClose}
                  className="text-gray-400 hover:text-white text-2xl"
                  aria-label="Close">×</button>
        </div>
        {beat?.title && (
          <p className="text-xs text-gray-400 mb-3 truncate">
            <span className="text-purple-300">"{beat.title}"</span> will be added to the playlist you pick below.
          </p>
        )}

        {/* Create new */}
        <form onSubmit={handleCreate} className="mb-3 flex gap-2"
              data-testid="create-beat-playlist-row">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="+ New playlist name…"
            className="bg-black/40 text-white border-gray-700 text-sm"
            data-testid="new-beat-playlist-name"
          />
          <Button
            type="submit"
            disabled={!newName.trim() || creating}
            className="bg-purple-600 hover:bg-purple-500 text-white"
            data-testid="create-beat-playlist-submit"
          >
            {creating ? "…" : "Create"}
          </Button>
        </form>

        {loading ? (
          <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
        ) : playlists.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            No beat playlists yet. Create one above to get started.
          </p>
        ) : (
          <div className="space-y-2">
            {playlists.map((pl) => {
              const already = (pl.beat_ids || []).includes(beat?.beat_id);
              const isBusy = busyId === pl.playlist_id;
              return (
                <button
                  key={pl.playlist_id}
                  onClick={() => !already && !isBusy && handleAdd(pl)}
                  disabled={already || isBusy}
                  data-testid={`beat-playlist-row-${pl.playlist_id}`}
                  className="w-full text-left flex items-center gap-3 p-2 rounded-lg bg-gray-900/40 border border-gray-800 hover:border-purple-500/40 disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-md bg-gray-800 overflow-hidden flex-shrink-0">
                    {pl.cover_image && (
                      <img src={optimizeImage(pl.cover_image, 'avatar')} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{pl.name}</p>
                    <p className="text-[11px] text-gray-500">
                      {pl.beat_count ?? (pl.beat_ids?.length || 0)} beats
                      {pl.is_public ? " · Public" : " · Private"}
                    </p>
                  </div>
                  <span className={`text-xs font-bold ${already ? "text-emerald-300" : "text-purple-300"}`}>
                    {already ? "✓ Added" : isBusy ? "…" : "+ Add"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddBeatToPlaylistDialog;
